import { makeStyles, withStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import React, { Dispatch, SetStateAction, useContext } from "react";
import { DARK_ACCENT, WHITE } from "../../../../constants";
import { ThemeContext } from "../../../../context";

interface Props {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
}

// Two separate static styled-components/hooks (one per theme) rather than one driven by a dynamic
// "function of props" color - same reasoning as UsersTable.tsx's identical split: this field's
// text/underline color needs to follow the app's light/dark theme, not stay hardcoded to WHITE
// (invisible against the light theme's own light background). Both are always instantiated (Rules
// of Hooks), and the component picks whichever matches the current theme.
const DarkTextField = withStyles({
  root: {
    "& .MuiInputBase-input": {
      color: WHITE,
      height: "28px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "#fff8", // Semi-transparent underline
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: WHITE, // Solid underline on hover
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: WHITE, // Solid underline on focus
    },
  },
})(TextField);

const LightTextField = withStyles({
  root: {
    "& .MuiInputBase-input": {
      color: DARK_ACCENT,
      height: "28px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: `${DARK_ACCENT}88`, // Semi-transparent underline
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: DARK_ACCENT, // Solid underline on hover
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: DARK_ACCENT, // Solid underline on focus
    },
  },
})(TextField);

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
const useDarkStyles = makeStyles({
  input: {
    color: `${WHITE} !important`,
    "&::before": {
      borderBottom: "1px solid #FFF",
    },
    "&::placeholder": {
      color: WHITE,
    },
  },
});

const useLightStyles = makeStyles({
  input: {
    color: `${DARK_ACCENT} !important`,
    "&::before": {
      borderBottom: `1px solid ${DARK_ACCENT}`,
    },
    "&::placeholder": {
      color: DARK_ACCENT,
    },
  },
});

const UserSearchField = (props: Props) => {
  const { value, setValue } = props;

  const { theme } = useContext(ThemeContext);
  const darkClasses = useDarkStyles();
  const lightClasses = useLightStyles();
  const classes = theme === "dark" ? darkClasses : lightClasses;
  const iconColor = theme === "dark" ? WHITE : DARK_ACCENT;
  const StyledTextField = theme === "dark" ? DarkTextField : LightTextField;

  return (
    <StyledTextField
      value={value}
      placeholder="Search"
      InputProps={{
        classes: { input: classes.input },
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setValue("")}>
              <CloseIcon style={{ color: iconColor }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputProps={{
        "data-test-id": "user-search-input",
      }}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

export default UserSearchField;
